/**
 * Pinia Store: Event Manager Booking
 *
 * Migrated from Vuex: src/extensions/eventManager/store/modules/booking.js
 */

import Vue from 'vue';
import { defineStore } from 'pinia';
import _ from 'lodash';

import BookingApi from '@/extensions/eventManager/store/api/booking';
import EventApi from '@/extensions/eventManager/store/api/event';
import RegistrationTemplateApi from '@/extensions/eventManager/store/api/registrationTemplate';
import { answersToValues, valuesToAnswers } from '@/extensions/eventManager/views/Partials/RegistrationFieldInputs';
import { useEventManagerEventStore } from '@/stores/eventManagerEvent';

const form = {
    id: undefined,
    event: '',
    booker: {
        id: undefined,
        gender: 'M',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        company: ''
    },
    slot: undefined,
    notify: true
};

const rules = {
    event: 'required',
    booker: {
        first_name: 'required',
        last_name: 'required',
        email: 'required|email'
    },
    slot: {
        id: 'required'
    }
};

const ui = {
    form: {
        initialized: false,
        validateOnEnter: false
    }
};

export const useEventManagerBookingStore = defineStore('eventManagerBooking', {
    state: () => ({
        loading: true,
        bookingsPaginated: {
            path: null,
            variables: {
                paginationSizes: [25, 50, 100]
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            defaultSort: null,
            link: {},
            items: []
        },
        options: {
            event: {
                loading: false,
                items: []
            }
        },
        selections: {
            event: undefined
        },
        // the registration form of the booked event and the answers to its custom questions
        template: {
            id: null,
            loading: false,
            fields: []
        },
        answers: {},
        form: _.cloneDeep(form),
        rules: _.cloneDeep(rules),
        ui: _.cloneDeep(ui)
    }),

    getters: {
        formInitialized: (state) => state.ui.form.initialized,
        formValidateOnEnter: (state) => state.ui.form.validateOnEnter
    },

    actions: {
        // Mutations converted to actions
        addPaginatedBookingsToState(data) {
            this.bookingsPaginated.items = data.items;
            this.bookingsPaginated.pageInfo = _.cloneDeep(data.pageInfo);
            this.bookingsPaginated.allColumns = _.cloneDeep(data.config.allColumns);
            this.bookingsPaginated.link = _.cloneDeep(data.config.link);
            this.bookingsPaginated.showFilters = _.get(data.config, 'showFilters') || false;
            this.bookingsPaginated.defaultSort = _.get(data.config, 'defaultSort') || null;
        },

        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            _.each(this.bookingsPaginated.allColumns, column => {
                let allowed = _.find(activeColumnKeys, value => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.bookingsPaginated.activeColumns = activeColumns;
            }
        },

        defineItemsPerPage(perPage) {
            this.bookingsPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.bookingsPaginated.pageInfo.currentPage = currentPage;
        },

        defineFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.bookingsPaginated.filters, key);
            } else {
                Vue.set(this.bookingsPaginated.filters, key, value);
            }
        },

        defineSorting({ key, value }) {
            this.bookingsPaginated.variables.order = key;
            this.bookingsPaginated.variables.direction = value;
        },

        defineFilterVisibility(value) {
            this.bookingsPaginated.showFilters = value;
        },

        updateLoading(value) {
            this.loading = value;
        },

        updateRules(newRules) {
            this.rules = newRules;
        },

        resetRules() {
            this.rules = _.cloneDeep(rules);
        },

        resetUI() {
            this.ui = _.cloneDeep(ui);
        },

        insertBooking(booking) {
            this.form = booking;
        },

        updateEventOptionsLoading(value) {
            this.options.event.loading = value;
        },

        insertEventOptions(events) {
            this.options.event.items = events;
        },

        // Form field setters
        setFormInitialized(flag) {
            this.ui.form.initialized = flag;
        },

        setFormValidateOnEnter(flag) {
            this.ui.form.validateOnEnter = flag;
        },

        setFormEvent(value) {
            this.form.event = value;
        },

        setFormBooker({ key, value }) {
            this.form.booker[key] = value;
        },

        setFormSlot(value) {
            this.form.slot = value;
        },

        setFormNotify(value) {
            this.form.notify = value;
        },

        // Selection setter
        setSelectionEvent(value) {
            this.selections.event = value;
        },

        // Registration template

        // Loads the event's registration form; the stored answers of the booking are mapped
        // onto its fields, and the optional fixed fields it marks required become rules.
        loadTemplateFields(templateId) {
            const id = templateId ? parseInt(templateId) : null;
            if (!id) {
                this.template = { id: null, loading: false, fields: [] };
                this.answers = {};
                this.applyTemplateRules();
                return Promise.resolve();
            }
            if (this.template.id === id && this.template.fields.length) return Promise.resolve();

            this.template.id = id;
            this.template.loading = true;
            return RegistrationTemplateApi.fetchRegistrationTemplate(id)
                .then(template => {
                    if (this.template.id !== id) return;
                    this.template.fields = _.get(template, 'fields') || [];
                    this.answers = valuesToAnswers(this.template.fields, _.get(this.form, 'values'));
                    this.applyTemplateRules();
                })
                .finally(() => {
                    this.template.loading = false;
                });
        },

        applyTemplateRules() {
            const newRules = _.cloneDeep(rules);
            const requiredFixed = { salutation: 'gender', phone: 'phone' };
            this.template.fields.forEach(field => {
                if (field.key && field.required && requiredFixed[field.key]) {
                    newRules.booker[requiredFixed[field.key]] = 'required';
                }
            });
            this.rules = newRules;
        },

        setAnswers(answers) {
            this.answers = answers;
            Vue.set(this.form, 'values', answersToValues(this.template.fields, answers));
            Vue.set(this.form, 'valuesChanged', true);
        },

        // Async actions
        fetchBookingsPaginated() {
            return BookingApi.fetchBookingsPaginated(this.bookingsPaginated).then(data => {
                this.addPaginatedBookingsToState(data);

                const activeColumnKeys = _.map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },

        fetchBooking(id) {
            return BookingApi.fetchBooking(id).then(booking => {
                this.insertBooking(_.cloneDeep(booking));
            });
        },

        fetchEventOptions(cmsID) {
            this.updateEventOptionsLoading(true);
            return EventApi.fetchList(cmsID)
                .then(events => {
                    this.updateEventOptionsLoading(false);
                    this.insertEventOptions(events);
                })
                .catch(err => {
                    this.updateEventOptionsLoading(false);
                    throw err;
                });
        },

        create(booking) {
            return BookingApi.create(booking).then(booking => {
                this.insertBooking(_.cloneDeep(booking));
                this.resetRules();
                return booking;
            });
        },

        update(booking) {
            return BookingApi.update(booking).then(booking => {
                this.insertBooking(_.cloneDeep(booking));
                this.resetRules();
                return booking;
            });
        },

        deleteBooking(id) {
            return BookingApi.delete(id).then(() => {
                this.reset();
                this.resetRules();
                this.resetUI();
            });
        },

        /**
         * Check a ticket in at the door.
         *
         * The mutation returns a payload rather than a booking now, because a ticket can admit to a
         * whole series and the server cannot always tell which session is being entered. This
         * unwraps it so the five existing callers keep receiving a booking, and turns everything
         * that is not a successful scan into a rejection carrying `status` and `sessions` - which
         * is what the pickers use to offer the operator a choice.
         */
        scanBooking(token, slotID = null) {
            return BookingApi.scan(token, slotID).then(result => {
                if (!result || result.status !== 'scanned') {
                    const error = new Error(_.get(result, 'message') || 'Scan failed');
                    error.status = _.get(result, 'status');
                    error.sessions = _.get(result, 'ticket.bookings') || [];
                    error.token = token;
                    throw error;
                }

                const booking = result.booking;
                // Paginated-Liste aktualisieren (standalone Bookings-View)
                const idx = this.bookingsPaginated.items.findIndex(b => b.id === booking.id);
                if (idx !== -1) Vue.set(this.bookingsPaginated.items, idx, booking);

                // Event-Store Slots aktualisieren (Bookings-Tab im Event-Formular)
                const eventStore = useEventManagerEventStore();
                const slots = _.get(eventStore, 'form.slots', []);
                slots.forEach((slot, slotIdx) => {
                    const bookingIdx = (slot.bookings || []).findIndex(b => b.id === booking.id);
                    if (bookingIdx !== -1) {
                        Vue.set(eventStore.form.slots[slotIdx].bookings, bookingIdx, {
                            ...booking,
                            slot: _.omit(slot, ['bookings']),
                        });
                    }
                });

                return booking;
            });
        },

        // Bestätigungsmail inkl. QR-Code erneut senden (keine Zustandsänderung an der Buchung).
        resendConfirmation(id) {
            return BookingApi.resendConfirmation(id);
        },

        reset() {
            this.insertBooking(_.cloneDeep(form));
            this.template = { id: null, loading: false, fields: [] };
            this.answers = {};
        }
    }
});
